<template>
  <main>
    <md-card>
      <md-card-header>
        <div class="md-title">請求管理</div>
      </md-card-header>

      <md-card-content>
        <div class="md-layout">
          <form class="md-layout-item md-size-50 md-small-size-100" novalidate @submit.prevent="searchSubmit">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-small-size-100">
                <md-field>
                  <label>請求月</label>
                  <md-select name="billingMonth" id="billingMonth" v-model="form.billing_month" required
                             :disabled="sending" ref="mdSelectBillingMonth">
                    <md-option v-for="monthAndYear in selectedBillingMonth" v-bind:key="monthAndYear.key"
                               :value="monthAndYear.key">
                      {{ monthAndYear.value }}
                    </md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout md-alignment-center-left">
                <md-button :disabled="sending" class="md-primary md-raised" type="submit">
                  <md-icon>search</md-icon>
                  表示する
                </md-button>
              </div>
            </div>
          </form>
          <div class="md-layout-item md-size-5 md-small-size-100">
            <div class="md-layout md-gutter"></div>
          </div>
          <div class="md-layout-item md-size-45 md-small-size-100">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-small-size-100">
                <md-field>
                  <label>{{ isNextMonth ? '請求額合計(税抜)' : '請求額合計(税込)' }}</label>
                  <md-input value="0" class="text-right" disabled readonly v-if="loadTotalBilled"></md-input>
                  <md-input :value="groupBilled" class="text-right" disabled readonly
                            v-else-if="isGroupAdmin()"></md-input>
                  <md-input :value="totalBilled" class="text-right" disabled readonly v-else></md-input>
                  <span class="md-suffix" v-if="isNextMonth">円（未確定）</span>
                  <span class="md-suffix" v-else>円</span>
                  <loading-spinner-overlay v-if="loadTotalBilled"></loading-spinner-overlay>
                </md-field>
              </div>
            </div>
          </div>
        </div>

        <loading-spinner v-if="loadBillings"></loading-spinner>
        <md-table v-else-if="pagination.total !== null">
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head v-for="(column, key) in tableColumns" v-bind:key="'column-key-' + key">
              <span v-if="isNextMonth">{{column.name}}</span>
              <a v-if="!isNextMonth" href="#" @click="sortBy(bills, key)"
                 v-bind:class="{'active' : sortKey === key}">{{column.name}}</a>
              <md-icon v-if="!isNextMonth && sortKey === key && statusSort === 'desc'">arrow_downward</md-icon>
              <md-icon v-else-if="!isNextMonth && sortKey === key && statusSort === 'asc'">arrow_upward</md-icon>
            </md-table-head>
            <md-table-head>請求書/明細書</md-table-head>
            <md-table-head></md-table-head>
          </md-table-row>
          <md-table-row v-if="billingList.length === 0 && paymentMethodList.length === 0">
            <md-table-cell colspan="9">{{noDataMessage}}</md-table-cell>
          </md-table-row>
          <md-table-row v-for="(bill, index) in billingList" v-bind:key="'bill-key-' + index">
            <md-table-cell>{{datetimeFormat(bill.billing_month + '01', 'YYYY年M月')}}</md-table-cell>
            <md-table-cell>{{bill.no}}</md-table-cell>
            <md-table-cell>{{bill.payment_method ? bill.payment_method.label : null}}</md-table-cell>
            <md-table-cell class="text-right">{{numberFormat(bill.total_billed)}}円</md-table-cell>
            <md-table-cell>{{bill.payment_method ? getBillMethodName(bill.payment_method.bill_method) : null}}
            </md-table-cell>
            <md-table-cell>{{paymentStatusLabel(bill.status)}}</md-table-cell>
            <md-table-cell>{{bill.payment_method ? getBillViaName(bill.payment_method.bill_via) : null}}</md-table-cell>
            <md-table-cell>
              <md-button :href="bill.pre_signed_url" class="md-raised" target="_blank" v-if="bill.pre_signed_url">
                <md-icon>
                  picture_as_pdf
                </md-icon>
                <span>ダウンロード</span>
              </md-button>
            </md-table-cell>
            <md-table-cell>
              <router-link :to="{name: 'BackendUserBillingDetail', params: {id: bill.id.toString()}}">
                <md-button class="md-raised">
                  <md-icon>description</md-icon>
                  <span>詳細</span>
                </md-button>
              </router-link>
            </md-table-cell>
          </md-table-row>
          <md-table-row v-for="(paymentMethod, index) in paymentMethodList" v-bind:key="'payment-method-key-' + index">
            <md-table-cell>{{paymentMethod.date}}</md-table-cell>
            <md-table-cell></md-table-cell>
            <md-table-cell>{{paymentMethod.label}}</md-table-cell>
            <md-table-cell class="text-right">{{numberFormat(paymentMethod.total_billed)}}円</md-table-cell>
            <md-table-cell>{{getBillMethodName(paymentMethod.bill_method)}}</md-table-cell>
            <md-table-cell>{{paymentStatusLabel(99)}}</md-table-cell>
            <md-table-cell>{{getBillViaName(paymentMethod.bill_via)}}</md-table-cell>
            <md-table-cell></md-table-cell>
            <md-table-cell></md-table-cell>
          </md-table-row>
        </md-table>
        <pagination v-if="!loadBillings && billingList.length"
                    :total="pagination.total"
                    :per-page="pagination.per_page"
                    :current-page="pagination.current_page"
                    @pagechanged="onPageChanged"
        />
      </md-card-content>
    </md-card>
  </main>
</template>

<script>
  import Pagination                                     from '../../../components/Pagination';
  import Common                                         from '../../../modules/common';
  import CommonMixin                                    from '../../../mixins/commonMixin';
  import {apiClientWithUserToken as apiClient, apiList} from '../../../modules/apiClient';
  import LoadingSpinner                                 from '../../../components/LoadingSpinner';
  import * as moment                                    from 'moment';
  import LoadingSpinnerOverlay                          from '../../../components/LoadingSpinnerOverlay';
  import {mapActions, mapGetters,}                      from 'vuex';

  const LIMIT = 1000;

  export default {
    name      : "UserBillings",
    mixins    : [CommonMixin],
    components: {LoadingSpinnerOverlay, LoadingSpinner, Pagination},
    data      : () => ({
      prices              : [],
      tax                 : [],
      activeAccount       : null,
      groupActiveAccounts : [],
      billingMonthTotal   : null,
      pagination          : {
        total       : null,
        per_page    : Common.limitPageDefault,
        current_page: 1,
      },
      form                : {
        group_id     : null,
        billing_month: null,
      },
      selectedBillingMonth: [],
      // Sort config
      sortKey             : 'billing_month',
      statusSort          : 'desc',
      bills               : [],
      paymentMethods      : [],
      accounts            : [],
      loadBillings        : false,
      isNextMonth         : true,
      loadTotalBilled     : false,
      nextBills           : [],
      totalBilled         : '0',
      groupBilled         : '0',
      registeredAt        : null,
    }),
    mounted   : function () {
      this.registeredAt         = this.registeredAtYearMonth();
      this.selectedBillingMonth = Common.billingMonthSelectionByLimit(this.registeredAt);
      this.form.billing_month   = this.selectedBillingMonth[0].key;
      this.checkBillingMonth();
      this.initDisplay();
    },
    updated() {
      if (this.$refs['mdSelectGroup'] && this.$refs['mdSelectGroup'].$el) {
        this.$refs['mdSelectGroup'].$el.validity = {};
      }
      if (this.$refs['mdSelectBillingMonth'] && this.$refs['mdSelectBillingMonth'].$el) {
        this.$refs['mdSelectBillingMonth'].$el.validity = {};
      }
    },
    computed  : {
      ...mapGetters('billings', {
        formData : 'formData',
        sortData : 'sortData',
        isRefresh: 'isRefresh',
        isBack   : 'isBack',
      }),
      paymentMethodList: function () {
        const self = this;
        if (self.form.group_id === null || self.form.group_id === self.unspecified) {
          return self.paymentMethods;
        }
        return self.paymentMethods.filter(paymentMethod => paymentMethod.group_ids !== undefined &&
          paymentMethod.group_ids.indexOf(self.form.group_id) >= 0);
      },
      billingList      : function () {
        const self = this;
        if (self.form.group_id === null || self.form.group_id === self.unspecified) {
          return self.bills;
        }
        return self.bills.filter(bill => bill.orders !== undefined &&
          bill.orders.filter(order => order.group_id === self.form.group_id).length > 0);
      },
      tableColumns: function () {
        return {
          billing_month: {name: '請求月', sort: 'desc'},
          no           : {name: '伝票番号', sort: 'desc'},
          label        : {name: '支払情報の名称', sort: 'asc'},
          total_billed : {name: this.isNextMonth ? '請求額(税抜)' : '請求額(税込)', sort: 'asc'},
          bill_method  : {name: '決済方法', sort: 'asc'},
          status       : {name: '支払状況', sort: 'asc'},
          bill_via     : {name: '請求書送付', sort: 'asc'},
        };
      },
    },
    methods   : {
      ...mapActions('billings', [
        'saveFormData',
        'saveSortData',
        'saveRefresh',
        'saveBack',
      ]),
      applyState() {
        if ((this.isRefresh || this.isBack) && (this.formData !== null || this.sortData !== null)) {
          if (this.formData !== null) {
            this.pagination.current_page = this.formData.page;
            this.pagination.per_page     = this.formData.limit;
            this.form.billing_month      = this.formData.billing_month;
          }
          if (this.sortData !== null) {
            this.sortKey    = this.sortData.sortKey;
            this.statusSort = this.sortData.statusSort;
          }
          this.searchSubmit();
        } else {
          this.sending = false;
        }
        this.saveRefresh(false);
        this.saveBack(false);
      },
      getPrice() {
        return new Promise((resolve, reject) => {
          apiClient
            .get(apiList.payment.price)
            .then(response => {
              if (response.data.errors) {
                reject(response);
              }
              resolve(response.data);
            })
            .catch((error) => reject(error))
        });
      },
      getActiveAccount() {
        return new Promise((resolve, reject) => {
          apiClient
            .get(apiList.accounts.active)
            .then(response => {
              if (response.data.errors) {
                reject(response);
              }
              resolve(response.data);
            })
            .catch((error) => reject(error))
        });
      },
      getAccounts() {
        return new Promise((resolve, reject) => {
          apiClient
            .get(apiList.accounts.list)
            .then(response => {
              if (response.data.errors) {
                reject(response);
              }
              resolve(response.data);
            })
            .catch((error) => reject(error))
        });
      },
      initDisplay() {
        if (this.isGroupAdmin()) {
          this.form.group_id = Common.getCurrentUserInfo().group.id;
        }
        const self           = this;
        this.sending         = true;
        this.loadTotalBilled = true;
        Promise
          .all([
            self.getPrice(),
            self.getActiveAccount(),
          ])
          .then(([paymentPriceResponse, activeAccountsResponse]) => {
            self.prices              = paymentPriceResponse.data;
            self.tax                 = paymentPriceResponse.tax;
            self.activeAccount       = activeAccountsResponse.total;
            self.groupActiveAccounts = activeAccountsResponse.items.filter(item => this.form.group_id === null || Number(item.group_id) === Number(this.form.group_id));
          })
          .then(() => {
            if (self.isNextMonth) {
              return self.getAccounts()
            }
            return Promise.resolve(null);
          })
          .then((result) => {
            if (result !== null) {
              self.accounts            = result.accounts.filter(account =>
                account.shikakumap_deregistered_at === null
              );
              self.activeAccount       = self.accounts.length;
              self.groupActiveAccounts = self.accounts.filter(account => {
                return self.form.group_id === null || account.group_id === Number(this.form.group_id)
              });
            }
          })
          .catch((error) => {
            if (error.data && self.hasResponseErrors(error)) {
              return;
            }
            self.errorMessages = [error.message];
          })
          .finally(() => {
            self.sending         = true;
            self.loadTotalBilled = false;
            self.calculateTotalBilled();
            self.applyState();
          })
      },
      /**
       * 請求額合計（税込）を算出する
       */
      calculateTotalBilled() {
        if (this.loadTotalBilled) {
          this.totalBilled = '0';
          this.groupBilled = '0';
          return;
        }

        // 当月はアクティブな人数 x 単価で算出する
        if (this.isNextMonth) {
          this.totalBilled = this.totalBilledByActiveAccount(this.activeAccount);
          this.groupBilled = this.isGroupAdmin()
            ? Common.numberFormat(this.totalBilledByPaymentAccount(this.groupActiveAccounts.length)) ?? '0'
            : this.totalBilled;
          return;
        }

        // 前月以前は一覧の請求額より合計する
        let billingTotal = 0;
        billingTotal += (this.bills.length === 0) ? 0 : this.bills.map(bill => bill.total_billed).reduce((prev, next) => prev + next);
        billingTotal += (this.nextBills.length === 0) ? 0 : this.nextBills.map(bill => bill.total_billed).reduce((prev, next) => prev + next);
        this.totalBilled = Common.numberFormat(billingTotal);
        this.groupBilled = this.totalBilled;
      },
      /**
       * Set meta pagination
       * @param meta
       */
      setMetaPagination(meta) {
        this.pagination.total        = meta.total;
        this.pagination.per_page     = meta.per_page;
        this.pagination.current_page = meta.current_page;
      },
      /**
       * Call api get billings
       */
      getBillings() {
        this.sending      = true;
        this.loadBillings = true;
        this.bills        = [];
        this.saveFormData({
          page         : this.pagination.current_page,
          limit        : this.pagination.per_page,
          billing_month: this.form.billing_month,
        });
        this.saveSortData({
          sortKey   : this.sortKey,
          statusSort: this.statusSort,
        });
        apiClient.get(apiList.jon.user_billings.billings, {
          params: {
            page         : this.pagination.current_page,
            limit        : this.pagination.per_page,
            billing_month: this.form.billing_month,
          }
        })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            const result = response.data;
            this.bills   = this.parseBillings(result.billings);
            this.setMetaPagination(result.meta)
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sending      = false;
            this.loadBillings = false;
            this.sortBy(this.bills);
            if (this.bills
              && this.bills.length > 0
              && this.pagination.total > LIMIT) {
              this.getNextBillings();
            } else {
              this.loadTotalBilled = false;
              this.calculateTotalBilled();
            }
          });
      },
      /** This is for calculation of total billed */
      getNextBillings() {
        this.nextBills = [];
        apiClient
          .get(apiList.jon.user_billings.billings, {
            params: {
              page         : this.pagination.current_page + 1,
              limit        : this.pagination.per_page,
              billing_month: this.form.billing_month,
            }
          })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            const result   = response.data;
            this.nextBills = result.billings;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.loadTotalBilled = false;
            this.calculateTotalBilled();
          });
      },
      /**
       * Change page table
       * @param page
       */
      onPageChanged(page) {
        this.pagination.current_page = page;
        this.getBillings();
      },
      /**
       * Submit search
       */
      searchSubmit() {
        this.checkBillingMonth();
        this.pagination.current_page = 1;
        this.totalBilled             = '0';
        this.groupBilled             = '0';
        this.loadTotalBilled         = true;
        if (this.isNextMonth) {
          this.bills = [];
          this.calculatePlanedPrice();
          return;
        }
        this.paymentMethods = [];
        this.getBillings();
      },
      parseBillings(billings) {
        const bills = [];
        billings
          .filter(billing => this.form.group_id === null || (billing.orders.filter(order => Number(order.group_id) === Number(this.form.group_id)).length > 0))
          .forEach(function (bill) {
            bill['label']       = bill.payment_method ? bill.payment_method.label : null;
            bill['bill_method'] = bill.payment_method ? bill.payment_method.bill_method : null;
            bill['bill_via']    = bill.payment_method ? bill.payment_method.bill_via : null;
            bills.push(bill);
          });
        return bills;
      },
      checkBillingMonth() {
        const nextMonth  = moment().add(1, 'months').format(Common.momentConst.dateFormatYearMonth);
        this.isNextMonth = this.form.billing_month === nextMonth;
      },
      registeredAtYearMonth() {
        const auth         = JSON.parse(localStorage.getItem(Common.localStorageKeys.user.auth));
        const registeredAt = auth ? auth.shikakumap_registered_at : null;
        if (!moment(registeredAt).isValid()) {
          return null;
        }
        return moment(registeredAt).format(Common.momentConst.dateFormatYearMonth);
      },
      calculatePlanedPrice() {
        this.paymentMethods = [];
        this.sending        = true;
        this.loadBillings   = true;
        this.saveFormData({
          page         : this.pagination.current_page,
          limit        : this.pagination.per_page,
          billing_month: this.form.billing_month,
        });
        this.saveSortData({
          sortKey   : this.sortKey,
          statusSort: this.statusSort,
        });
        const self = this;
        self
          .getPaymentMethods()
          .then((result) => {
            for (let paymentMethod of result.data) {
              paymentMethod.total_billed = null;
              self.paymentMethods.push(paymentMethod);
            }
            self.setMetaPagination({
              total       : self.paymentMethods.length,
              per_page    : self.pagination.per_page,
              current_page: 1,
            });
          })
          .then(() => {
            if (self.isNextMonth && !(self.accounts && self.accounts.length > 0)) {
              return self.getAccounts();
            }
            return Promise.resolve([]);
          })
          .then(() => {
            if (self.paymentMethods.length > 0) {
              return self.getPaymentSettings();
            }
            return Promise.resolve([]);
          })
          .then((result) => {
            self.paymentMethods  = self.paymentMethods.filter(paymentMethod => {
              const setting = result.data.filter(paymentSetting => {
                return self.form.group_id === null ||
                  (paymentSetting.payment_method.gmo_member_id === paymentMethod.gmo_member_id &&
                    Number(self.form.group_id) === Number(paymentSetting.group_id));
              });
              return setting.length > 0;
            });
            self.loadTotalBilled = false;
            self.calculateTotalBilled();
            let total            = 0;
            self.paymentMethods.forEach(function (paymentMethod) {
              paymentMethod.date         = self.datetimeFormat(self.form.billing_month + '01', 'YYYY年M月')
              const setting              = result.data.filter(paymentSetting => {
                return paymentSetting.payment_method.gmo_member_id === paymentMethod.gmo_member_id;
              })
              paymentMethod.total_billed = 0;
              if (setting.length > 0) {
                const groupIds             = self.isGroupAdmin()
                  ? [self.form.group_id]
                  : setting.map(setting => setting.group_id);
                const activeAccount        = self.accounts.filter(function (account) {
                  return groupIds.indexOf(account.group_id) >= 0 && account.shikakumap_deregistered_at === null;
                })
                paymentMethod.group_ids    = groupIds;
                paymentMethod.total_billed = self.totalBilledByPaymentAccount(activeAccount.length);
                total += paymentMethod.total_billed;
              }
            })
            self.totalBilled = Common.numberFormat(total) ?? '0';
            self.groupBilled = self.totalBilled;
          })
          .catch((error) => {
            if (error.data && self.hasResponseErrors(error)) {
              return;
            }
            self.errorMessages = [error.message];
          })
          .finally(() => {
            self.sending      = false;
            self.loadBillings = false;
          });
      },
      getPaymentMethods() {
        return new Promise((resolve, reject) => {
          apiClient
            .get(apiList.payment.payment_methods.list)
            .then(response => {
              if (response.data.errors) {
                reject(response);
              }
              resolve(response.data);
            })
            .catch((error) => reject(error))
        });
      },
      getPaymentSettings() {
        return new Promise((resolve, reject) => {
          apiClient
            .get(apiList.payment.settings.list)
            .then(response => {
              if (response.data.errors) {
                reject(response);
              }
              resolve(response.data);
            })
            .catch((error) => reject(error))
        });
      },
      totalBilledByActiveAccount(activeAccount, withTax = false) {
        const billingTotal = Common.priceCalculator(this.prices, activeAccount);
        const unitPrice    = Common.unitPriceCalculator(billingTotal, activeAccount);
        const totalPrice   = Common.consumptionTaxCalculator(unitPrice, activeAccount, withTax ? this.tax.rate : 0);
        return Common.numberFormat(totalPrice) ?? '0';
      },
      totalBilledByPaymentAccount(paymentAccount) {
        if (paymentAccount === 0) {
          return 0;
        }
        return Math.floor(Number(this.totalBilled.replace(',', '')) / this.activeAccount * paymentAccount);
      },
    },
  }
</script>

<style lang="scss" scoped>
  .md-theme-default a:not(.md-button) {
    color: black;
  }

  a.active {
    font-weight: bold;
    color: black;
  }

  .text-right {
    text-align: right;
  }
</style>
